import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { useOperatingHour } from '../../hooks/operatingHours';
import {
  CLOSED_HOURS_SEARCH_DEFAULTS,
  parseClosedHoursSearch,
} from '../../hooks/useClosedHoursRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type ClosedHoursManagement from '../../components/ClosedHoursManagement/index';
import type EditClosedHour from '../../components/ClosedHoursManagement/EditClosedHour';

export const closedHoursIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/closed_hours',
  validateSearch: parseClosedHoursSearch,
  search: { middlewares: [stripSearchParams(CLOSED_HOURS_SEARCH_DEFAULTS)] },
  component: function ClosedHoursIndexRoute() {
    const { t } = useTranslation();
    const { currentUser } = useAppRouterInitial();
    useDocumentTitle(t('common.page_titles.closed_hours'));
    const [Component, setComponent] = useState<typeof ClosedHoursManagement | null>(null);
    useEffect(() => {
      import('../../components/ClosedHoursManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_closed_hours')} />;
    }
    return <Component currentUser={currentUser} />;
  },
});

export const closedHoursEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/closed_hours/$id/edit',
  // RBAC: edit is a write-only surface. The guard redirects read-only users
  // (no operating_hours.write) to the index before this route's component or
  // loader runs, so they never reach the editable form (or fire its fetch)
  // even via a direct URL. The server policy enforces this too; this is UX.
  beforeLoad: requireCapability(CAPABILITIES.OPERATING_HOURS_WRITE, {
    to: '/closed_hours',
  }),
  component: function ClosedHoursEditRoute() {
    const params = closedHoursEditRoute.useParams();
    const { initialClosedHour } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialClosedHour && initialClosedHour.id === idNum ? initialClosedHour : undefined;
    // In-router nav from the index arrives without a seed; fetch so the form
    // always receives a defined closed hour.
    const { data } = useOperatingHour(idNum, {
      initialData: seed ? { operating_hour: seed } : undefined,
    });
    useDocumentTitle(t('common.page_titles.closed_hours'));
    const [Component, setComponent] = useState<typeof EditClosedHour | null>(null);
    useEffect(() => {
      import('../../components/ClosedHoursManagement/EditClosedHour').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const closedHour = data?.operating_hour;
    if (!Component || !closedHour) {
      return <EditSkeleton label={t('common.loading_closed_hours')} />;
    }
    return <Component closedHour={closedHour} />;
  },
});
